// ------------------------------
// helpers/UsdtDisclaimer.tsx
// USDT risk disclaimer component
// ------------------------------

import { observer } from "mobx-react";
import React from "react";
import {
  Text,
  Stack,
  Alert,
  Button,
  Divider,
  Image,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";

const YEARS_SINCE_1913 = new Date().getFullYear() - 1913; // e.g. 2026 => 113

interface UsdtDisclaimerProps {
  onAgree: () => void;
  buttonText: string;
}

const UsdtDisclaimer = observer(
  ({ onAgree, buttonText }: UsdtDisclaimerProps) => {
    return (
      <Stack>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="USDT Payments Not Secured By Bitcoin"
          color="yellow"
        >
          <Text size="sm" mb="md">
            Rizful supports small outbound USDT payments as an accomodation for
            our users in Africa, South Asia, and Latin America.
          </Text>

          <Text size="sm" mb="md">
            If you must use USDT, consider using it only for small amounts, and
            moving your funds back into Bitcoin as soon as possible.
          </Text>

          <Divider my="sm" />

          <Title order={5} mb={6}>
            Risk of devaluation
          </Title>

          <Text size="sm" mb="xs">
            The US dollar has lost <b>~97%</b> of its purchasing power in the
            last <b>{YEARS_SINCE_1913}</b> years.
          </Text>

          <Image
            src="https://daxwktpz4hkwd.cloudfront.net/vault_images/usd-purchasing-power.png"
            alt="USD purchasing power over time"
            radius="md"
            mb={6}
          />

          <Text size="xs" c="dimmed" mb="md">
            Purchasing power of USD over time (CPI-based).
          </Text>

          <Divider my="sm" />

          <Title order={5} mb={6}>
            Tether Risks
          </Title>

          <Text size="sm" mb="md">
            When you use USDT, you are entirely reliant on <b>Tether</b> (a
            private corporation) to maintain their system, honor redemptions,
            manage reserves, and keep the token pegged.
          </Text>

          <Divider my="sm" />

          <Title order={5} mb={6}>
            Solana Network Risks
          </Title>

          <Text size="sm" mb="md">
            This USDT is sent on the <b>Solana</b> network, which is a
            centrally-controlled blockchain. Unlike Bitcoin, Solana has been
            halted multiple times by its operators, transactions can be
            censored, and the network is controlled by a small group of
            validators.
          </Text>
          <Text size="sm" mb="md">
            Solana offers no meaningful decentralization benefits over a
            traditional database. Use it only when necessary and move funds to
            Bitcoin as soon as possible.
          </Text>

          <Divider my="sm" />

          <Title order={5} mb={6}>
            Always Start With A $1 Test Transaction
          </Title>

          <Text size="sm" mb="md">
            Solana has many different address formats and standards. When
            sending USDT to a new address, you <b>MUST</b> always send a small
            $1 test transaction first and confirm it was received before sending
            the full amount. USDT payments are irreversible - if you send to an
            incompatible or incorrect address, your funds will be permanently
            lost.
          </Text>

          <Button onClick={onAgree} color="yellow" variant="filled">
            {buttonText}
          </Button>
        </Alert>
      </Stack>
    );
  },
);

export default UsdtDisclaimer;
